import { useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isPast,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@taskflow/ui';
import { parseNullableInstant } from '@taskflow/client';
import { cn } from '../../lib/cn.js';
import { useIsDesktop } from '../../lib/use-media-query.js';
import { PRIORITY_SWATCH, PRIORITY_BORDER, PRIORITY_TINT } from './priority-colors.js';
import type { CardSummary } from './api.js';

/**
 * The calendar view (§10.4's own Calendar/Timeline surface).
 *
 * Month grid over the SAME `cardsQuery` result board/list/table already
 * read — `board-page.tsx` fetches it once and hands it to whichever view is
 * showing, so switching views stays instant and the views can never disagree
 * about which cards match the current filter. Bucketed by `dueDate` alone:
 * this is "what's due when", not a Gantt-style span view over
 * `startDate`/`dueDate` ranges — `cardsQuery`'s summary shape doesn't even
 * carry `startDate` (see `card-tile.tsx`'s own note on that), and a true
 * timeline is a materially bigger surface than a due-date calendar.
 *
 * The month being VIEWED is local component state, not the URL — unlike
 * `view`/`filter`/`groupBy`/`sortBy` (§10.5), which board-page.tsx owns.
 * Which month someone happens to be looking at is not part of "what this
 * board shows", the same way `table-view.tsx`'s own scroll position isn't;
 * a link to this board should open on the current month, not wherever the
 * last viewer scrolled to.
 *
 * ## Two renderers, one data model
 *
 * The 7-column grid reads fine at desktop width — a cramped `min-h-24`
 * cell with 3 rows of truncated titles does not survive a phone's width,
 * found from a real report rather than a design review. Below `md:`
 * (`useIsDesktop`, the same 768px breakpoint the rest of the app already
 * coordinates its own responsive layouts against) this renders an AGENDA
 * instead: one row per day that actually has a card, chronological, full
 * titles, no truncation, no cramped 7-across math to fit into a phone's
 * width. Both read the identical `byDay` map built once above them —
 * there is no second query, no second bucketing pass, just two ways of
 * laying the same buckets out.
 */

export interface CalendarViewProps {
  readonly cards: readonly CardSummary[];
  readonly onOpenCard: (cardId: string) => void;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** How many cards a day cell shows before collapsing the rest into a count. */
const MAX_VISIBLE_PER_DAY = 3;

function dayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** A day strictly before today with at least one card — the calendar's one
    color cue for "this was due and the day has already passed," the same
    date-only definition `lib/format.ts`'s `formatDueDate` uses for a card
    tile's own overdue badge (never accounting for the card's own status —
    see that helper's own precedent, matched here rather than invented). */
function isOverdueDay(day: Date, dayCards: readonly CardSummary[]): boolean {
  return dayCards.length > 0 && isPast(day) && !isToday(day);
}

export function CalendarView({ cards, onOpenCard }: CalendarViewProps) {
  // A lazy initializer, not a bare `new Date()` in the render body — the
  // React Compiler purity rule flags reading the clock during render;
  // `formatDueDate`/`hasPassed` in lib/format.ts exist for the identical
  // reason. This runs once, to seed which month opens first.
  const [month, setMonth] = useState(() => new Date());
  const isDesktop = useIsDesktop();

  const byDay = useMemo(() => {
    const map = new Map<string, CardSummary[]>();
    for (const card of cards) {
      const due = parseNullableInstant(card.dueDate);
      if (due === null) continue;
      const key = dayKey(due);
      const bucket = map.get(key);
      if (bucket) bucket.push(card);
      else map.set(key, [card]);
    }
    return map;
  }, [cards]);

  const unscheduledCount = cards.filter((card) => card.dueDate === null).length;

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [month]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-line/40 px-4 py-2.5 sm:px-6">
        <h2 className="text-sm font-semibold text-ink">{format(month, 'MMMM yyyy')}</h2>

        <div className="flex items-center gap-0.5">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => {
              setMonth((current) => subMonths(current, 1));
            }}
            className="rounded p-1 text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            <ChevronLeft aria-hidden="true" className="size-4" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => {
              setMonth(new Date());
            }}
            className="rounded px-2 py-1 text-xs font-medium text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            Today
          </button>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => {
              setMonth((current) => addMonths(current, 1));
            }}
            className="rounded p-1 text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            <ChevronRight aria-hidden="true" className="size-4" strokeWidth={2} />
          </button>
        </div>

        {unscheduledCount > 0 && (
          <span className="text-xs text-ink-faint sm:ml-auto">
            {unscheduledCount} {unscheduledCount === 1 ? 'card has' : 'cards have'} no due date
          </span>
        )}
      </div>

      {isDesktop ? (
        <MonthGrid days={days} month={month} byDay={byDay} onOpenCard={onOpenCard} />
      ) : (
        <AgendaList days={days} byDay={byDay} onOpenCard={onOpenCard} />
      )}
    </div>
  );
}

function MonthGrid({
  days,
  month,
  byDay,
  onOpenCard,
}: {
  readonly days: readonly Date[];
  readonly month: Date;
  readonly byDay: ReadonlyMap<string, CardSummary[]>;
  readonly onOpenCard: (cardId: string) => void;
}) {
  return (
    <>
      <div className="grid grid-cols-7 text-[11px] font-medium text-ink-faint">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="border-b border-line/30 px-2 py-1.5 text-center">
            {label}
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 auto-rows-fr grid-cols-7 overflow-y-auto border-l border-t border-line/30">
        {days.map((day) => {
          const key = dayKey(day);
          const dayCards = byDay.get(key) ?? [];
          const inMonth = isSameMonth(day, month);
          const overflow = dayCards.length - MAX_VISIBLE_PER_DAY;
          const overdueDay = isOverdueDay(day, dayCards);

          return (
            <div
              key={key}
              className={cn(
                'min-h-24 border-b border-r border-line/30 p-1 transition-colors',
                !inMonth && 'bg-surface-sunken/40',
                isToday(day) && 'bg-accent/[0.03]',
              )}
            >
              <div className="mb-1 flex items-center justify-between px-1">
                <span
                  className={cn(
                    'text-[11px]',
                    isToday(day)
                      ? 'flex size-5 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-accent-ink'
                      : overdueDay
                        ? 'font-medium text-danger'
                        : inMonth
                          ? 'text-ink-muted'
                          : 'text-ink-faint',
                  )}
                >
                  {format(day, 'd')}
                </span>
                {dayCards.length > MAX_VISIBLE_PER_DAY && (
                  <span className="text-[9px] text-ink-faint">
                    +{dayCards.length - MAX_VISIBLE_PER_DAY}
                  </span>
                )}
              </div>

              <div className="space-y-0.5">
                {dayCards.slice(0, MAX_VISIBLE_PER_DAY).map((card) => (
                  <CardRow key={card.cardId} card={card} onOpenCard={onOpenCard} />
                ))}
                {overflow > 0 && (
                  <PopoverRoot>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-full rounded px-1 text-left text-[10px] text-ink-faint hover:bg-surface-hover hover:text-ink hover:underline"
                      >
                        +{overflow} more
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-64 p-2">
                      <p className="mb-1 px-1 text-[11px] font-medium text-ink-muted">
                        {format(day, 'EEEE, d MMMM')}
                      </p>
                      <ul className="max-h-64 space-y-0.5 overflow-y-auto">
                        {dayCards.map((card) => (
                          <li key={card.cardId}>
                            <CardRow card={card} onOpenCard={onOpenCard} />
                          </li>
                        ))}
                      </ul>
                    </PopoverContent>
                  </PopoverRoot>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function AgendaList({
  days,
  byDay,
  onOpenCard,
}: {
  readonly days: readonly Date[];
  readonly byDay: ReadonlyMap<string, CardSummary[]>;
  readonly onOpenCard: (cardId: string) => void;
}) {
  const rows = days
    .map((day) => ({ day, dayCards: byDay.get(dayKey(day)) ?? [] }))
    .filter((row) => row.dayCards.length > 0);

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-xl bg-surface-sunken/50">
            <span className="text-2xl">📅</span>
          </div>
          <p className="text-sm font-medium text-ink-muted">Nothing due this month</p>
          <p className="mt-1 text-xs text-ink-faint">Cards with due dates will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {rows.map(({ day, dayCards }, idx) => {
        const overdueDay = isOverdueDay(day, dayCards);
        const today = isToday(day);
        const urgentCount = dayCards.filter((c) => c.priority === 'urgent').length;

        return (
          <div
            key={dayKey(day)}
            className={cn(
              'relative px-4 py-3 sm:px-6',
              idx > 0 && 'border-t border-line/20',
              today && 'bg-accent/[0.03]',
            )}
          >
            {/* Today indicator line */}
            {today && <div className="absolute inset-y-0 left-0 w-0.5 bg-accent" />}

            {/* Day header */}
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {today ? (
                  <span className="flex size-6 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-accent-ink">
                    {format(day, 'd')}
                  </span>
                ) : (
                  <span className="text-[11px] font-medium text-ink-faint">{format(day, 'd')}</span>
                )}
                <div>
                  <p
                    className={cn(
                      'text-[13px] font-semibold',
                      today ? 'text-accent' : overdueDay ? 'text-danger' : 'text-ink',
                    )}
                  >
                    {format(day, 'EEEE')}
                  </p>
                  <p className="text-[10px] text-ink-faint">{format(day, 'MMMM yyyy')}</p>
                </div>
              </div>

              {/* Card count badge */}
              <div className="flex items-center gap-1.5">
                {urgentCount > 0 && (
                  <span className="flex items-center gap-1 rounded-full bg-priority-urgent/15 px-2 py-0.5 text-[10px] font-medium text-priority-urgent">
                    <span className="size-1.5 rounded-full bg-priority-urgent" />
                    {urgentCount} urgent
                  </span>
                )}
                <span className="rounded-full bg-surface-sunken/60 px-2 py-0.5 text-[10px] font-medium text-ink-faint">
                  {dayCards.length} {dayCards.length === 1 ? 'card' : 'cards'}
                </span>
              </div>
            </div>

            {/* Cards */}
            <ul className="space-y-1.5 pl-1">
              {dayCards.map((card) => (
                <li key={card.cardId}>
                  <CardRow card={card} onOpenCard={onOpenCard} agenda />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function CardRow({
  card,
  onOpenCard,
  agenda = false,
}: {
  readonly card: CardSummary;
  readonly onOpenCard: (cardId: string) => void;
  readonly agenda?: boolean;
}) {
  const borderColor = card.priority !== null ? PRIORITY_BORDER[card.priority] : 'border-ink-faint';
  const bgColor = card.priority !== null ? PRIORITY_TINT[card.priority] : 'bg-ink-faint/10';
  const dotColor = card.priority !== null ? PRIORITY_SWATCH[card.priority] : 'bg-ink-faint';

  if (agenda) {
    return (
      <button
        type="button"
        onClick={() => {
          onOpenCard(card.cardId);
        }}
        title={card.title}
        className={cn(
          'group flex w-full items-center gap-2.5 rounded-lg border-l-[3px] py-2 pl-3 pr-2 text-left transition-all',
          'hover:translate-x-0.5 hover:shadow-sm',
          borderColor,
          bgColor,
        )}
      >
        {/* Priority dot */}
        <span className={cn('size-2 shrink-0 rounded-full', dotColor)} />

        {/* Reference */}
        <span className="shrink-0 font-mono text-[11px] font-medium text-ink-faint">
          {card.reference}
        </span>

        {/* Title */}
        <span className="min-w-0 flex-1 truncate text-[13px] text-ink group-hover:text-ink">
          {card.title}
        </span>

        {/* Hover arrow */}
        <span className="size-4 shrink-0 text-ink-faint opacity-0 transition-opacity group-hover:opacity-100">
          →
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        onOpenCard(card.cardId);
      }}
      title={card.title}
      className={cn(
        'group flex w-full items-center gap-1.5 truncate rounded-r text-left transition-colors',
        'py-1 pl-1.5 pr-1 text-[11px]',
        'border-l-[3px] hover:brightness-110',
        borderColor,
        bgColor,
      )}
    >
      <span className="truncate text-ink">{card.title}</span>
    </button>
  );
}
